package org.example.project.registrierung

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.browser.window
import org.example.project.login.LoginStyles
import org.example.project.login.UnifiedAccessStyles
import org.example.project.login.UnifiedAccessButton
import org.example.project.login.UnifiedLoginContent
import org.example.project.login.UnifiedLoginLayout
import org.example.project.service.LoginService
import org.example.project.service.PasswordPolicy
import org.example.project.settings.PasswordRequirementHint
import org.example.project.settings.LogoSettings
import org.example.project.service.PasswordResetApi
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxHeight
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.paddingTop
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event

@Composable
fun NetzwerkLogin(
    onLoginComplete: () -> Unit,
    onRegister: () -> Unit
) {
    Style(LoginStyles)
    Style(UnifiedAccessStyles)
    val scope = rememberCoroutineScope()
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var snapKey by remember { mutableStateOf("") }
    val verificationMode = remember { window.location.search.substringAfter("?", "").contains("verifizierung=1") }
    var verificationCredentialsEntered by remember { mutableStateOf(false) }
    var isMobile by remember { mutableStateOf(window.innerWidth <= 768) }
    var loading by remember { mutableStateOf(false) }
    var loginEmailError by remember { mutableStateOf<String?>(null) }
    var loginPwError by remember { mutableStateOf<String?>(null) }
    var newPassword by remember { mutableStateOf("") }
    var repeatPassword by remember { mutableStateOf("") }
    var pwError by remember { mutableStateOf<String?>(null) }
    var pwUser by remember { mutableStateOf<org.example.project.utils.All?>(null) }
    var resetMessage by remember { mutableStateOf<String?>(null) }
    var resetLoading by remember { mutableStateOf(false) }
    var zweiFaktorCode by remember { mutableStateOf("") }
    var zweiFaktorToken by remember { mutableStateOf("") }
    var zweiFaktorEinrichtung by remember { mutableStateOf(false) }
    var zweiFaktorQrCode by remember { mutableStateOf("") }
    var trustedDevice by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        val resizeListener: (Event) -> Unit = { isMobile = window.innerWidth <= 768 }
        window.addEventListener("resize", resizeListener)
        onDispose { window.removeEventListener("resize", resizeListener) }
    }
    fun handleAuthenticatedUser(user: org.example.project.utils.All) {
        LoginService.captureSession(user)
        if (user.zweiFaktorErforderlich) {
            zweiFaktorToken = user.zweiFaktorToken
            zweiFaktorEinrichtung = user.zweiFaktorEinrichtung
            zweiFaktorQrCode = user.zweiFaktorQrCode
            zweiFaktorCode = ""
            trustedDevice = false
            loginPwError = null
            return
        }
        if (!user.passwortVerifiziert) {
            pwUser = user
            pwError = null
            newPassword = ""
            repeatPassword = ""
            return
        }
        LoginService.login(user)
        onLoginComplete()
    }

    fun doLogin() {
        val mailSnap = email.lowercase()
        if (mailSnap.isEmpty() || password.isEmpty() || loading) return
        if (verificationCredentialsEntered && snapKey.isBlank()) return
        loading = true
        loginEmailError = null
        loginPwError = null
        scope.launch {
            try {
                val user = if (verificationCredentialsEntered) {
                    DesktopRegistrierungApi.verifyDesktopAccess(
                        mailSnap,
                        password,
                        snapKey
                    )
                } else {
                    DesktopRegistrierungApi.loginDetailed(mailSnap, password).let { result ->
                        if (result.error == "build_reload") return@launch
                        if (result.user == null) {
                            val message = when (result.error) {
                                "email_invalid" -> "E-Mail-Adresse ist ungültig."
                                "email_unknown" -> "E-Mail ist nicht bekannt."
                                "password_invalid" -> "Passwort falsch."
                                "rate_limited" -> "Zu viele Anmeldeversuche. Bitte 60 Sekunden warten."
                                "user_blocked" -> "Der Benutzerzugang ist gesperrt."
                                "desktop_access_denied" -> "Desktop-Zugang ist nicht freigeschaltet."
                                "company_blocked" -> "Der Unternehmenszugang ist gesperrt."
                                "company_not_active" -> "Der Unternehmenszugang ist noch nicht aktiv."
                                "network_error" -> "Server nicht erreichbar. Bitte Verbindung prüfen."
                                else -> "Anmeldung ist derzeit technisch nicht möglich."
                            }
                            loginPwError = message
                        }
                        result.user
                    }
                }
                if (user == null) {
                    if (loginPwError == null) {
                        loginPwError = if (verificationCredentialsEntered) "SnapKey stimmt nicht" else "Anmeldung fehlgeschlagen."
                    }
                    return@launch
                }
                if (!verificationCredentialsEntered && !user.aktiv) {
                    verificationCredentialsEntered = true
                    return@launch
                }
                if (user.gesperrt == true) {
                    loginPwError = "Du wurdest vom Administrator gesperrt."
                    return@launch
                }
                if (!user.zugangDesktop) {
                    loginPwError = "Desktop-Zugang ist für diesen User deaktiviert."
                    return@launch
                }
                handleAuthenticatedUser(user)
            } finally {
                loading = false
            }
        }
    }

    fun doZweiFaktor() {
        if (loading) return
        if (zweiFaktorCode.length != 6) {
            loginPwError = "Bitte 6-stelligen Code eingeben"
            return
        }
        loading = true
        loginPwError = null
        scope.launch {
            try {
                val user = DesktopRegistrierungApi.checkZweiFaktor(zweiFaktorToken, zweiFaktorCode, trustedDevice)
                if (user == null) {
                    loginPwError = "Code abgelaufen oder ungültig"
                    return@launch
                }
                if (user.zweiFaktorFehler) {
                    loginPwError = if (user.zweiFaktorGesperrt) {
                        "Der Zugang wurde gesperrt. Eine Reset-E-Mail wurde versendet."
                    } else {
                        "Code falsch. Verbleibende Versuche: ${user.zweiFaktorVerbleibendeVersuche}"
                    }
                    return@launch
                }
                zweiFaktorToken = ""
                zweiFaktorCode = ""
                zweiFaktorQrCode = ""
                zweiFaktorEinrichtung = false
                trustedDevice = false
                LoginService.captureSession(user)
                LoginService.login(user)
                onLoginComplete()
            } finally {
                loading = false
            }
        }
    }
    fun requestPasswordReset() {
        val mail = email.trim().lowercase()
        if (mail.isBlank() || resetLoading) {
            loginEmailError = "Bitte E-Mail eingeben"
            return
        }
        resetLoading = true
        resetMessage = null
        loginEmailError = null
        scope.launch {
            try {
                PasswordResetApi.request(mail, "desktop")
                resetMessage = "Wenn ein Konto existiert, wurde eine E-Mail versendet."
            } catch (_: Throwable) {
                resetMessage = "E-Mail konnte nicht versendet werden."
            } finally {
                resetLoading = false
            }
        }
    }

    fun doPasswordChange() {
        val user = pwUser ?: run {
            pwError = "Kein User-Kontext"
            return
        }
        val qualityError = PasswordPolicy.error(newPassword)
        if (qualityError != null) {
            pwError = qualityError
            return
        }
        if (newPassword != repeatPassword) {
            pwError = "Passwörter nicht identisch"
            return
        }
        if (loading) return
        loading = true
        pwError = null
        scope.launch {
            try {
                val updated = DesktopRegistrierungApi.changePassword(user.id, user.firmenId, newPassword) ?: run {
                    pwError = "Speichern fehlgeschlagen"
                    return@launch
                }
                pwUser = null
                handleAuthenticatedUser(updated)
            } catch (_: Throwable) {
                pwError = "Speichern fehlgeschlagen"
            } finally {
                loading = false
            }
        }
    }

    if (zweiFaktorToken.isBlank() && pwUser == null && !verificationCredentialsEntered) {
        UnifiedLoginLayout(isMobile) {
            UnifiedLoginContent(
                identifier = email,
                password = password,
                identifierPlaceholder = "E-Mail",
                loading = loading,
                errorText = loginEmailError ?: loginPwError,
                forgotLabel = if (verificationMode) null else if (resetLoading) "..." else "Passwort vergessen",
                switchPrefix = if (verificationMode) null else "Neu hier?",
                switchLabel = if (verificationMode) null else "Registrieren",
                onIdentifierChange = { email = it; loginEmailError = null },
                onPasswordChange = { password = it; loginPwError = null },
                onSubmit = ::doLogin,
                onForgot = if (verificationMode) null else ::requestPasswordReset,
                onSwitch = if (verificationMode) null else onRegister
            )
        }
        return
    }

    Div({
        classes(NetzwerkLoginStyles.gifWrapper)
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(0.px)
            width(100.percent)
            if (isMobile) {
                height(100.percent)
                padding(16.px)
                property("box-sizing", "border-box")
            }
        }
    }) {
        if (!isMobile) {
            Div({ classes(UnifiedAccessStyles.mapPane) }) {
                NetzwerkTeilnehmerUebersicht(compact = true)
            }
            Div({ classes(UnifiedAccessStyles.divider) })
        }
        Div({
            classes(LoginStyles.rightPane, NetzwerkLoginStyles.loginFormReveal, UnifiedAccessStyles.panel, UnifiedAccessStyles.mobilePanel)
        }) {
            Img(src = LogoSettings.desktopRegistrationHeaderLogo, attrs = {
                classes(UnifiedAccessStyles.logo)
            })
            if (zweiFaktorToken.isNotBlank()) {
                H2 { Text("Zwei-Faktor-Code") }
                P({ classes(NetzwerkLoginStyles.subheading); style { marginBottom(20.px) } }) {
                    Text(
                        if (zweiFaktorEinrichtung) {
                            "QR-Code scannen und danach den 6-stelligen Code eingeben."
                        } else {
                            "Bitte den 6-stelligen Code aus deiner Authenticator-App eingeben."
                        }
                    )
                }
                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                    if (zweiFaktorEinrichtung && zweiFaktorQrCode.isNotBlank()) {
                        Img(src = zweiFaktorQrCode, attrs = {
                            style {
                                width(180.px)
                                height(180.px)
                                property("align-self", "center")
                            }
                        })
                    }
                    NetzwerkInput(
                        InputType.Text,
                        zweiFaktorCode,
                        "6-stelliger Code",
                        loginPwError,
                        {
                            zweiFaktorCode = it.filter { char -> char in '0'..'9' }.take(6)
                            loginPwError = null
                        },
                        ::doZweiFaktor,
                        loading,
                        loginStyle = true
                    )
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(10.px)
                            width(100.percent)
                        }
                    }) {
                        LumaticaCheckbox(
                            checked = trustedDevice,
                            onChange = { trustedDevice = it },
                            enabled = !loading,
                            fontSizePx = 15
                        )
                        Span({ classes(NetzwerkLoginStyles.subheading) }) {
                            Text("Diesem Gerät vertrauen")
                        }
                    }
                    Div({ style { width(100.percent); marginTop(20.px) } }) {
                        LumaticaButton(
                            label = if (loading) "..." else "Code prüfen",
                            onClick = ::doZweiFaktor,
                            variant = LumaticaButtonVariant.Action,
                            enabled = !loading,
                            fillWidth = true,
                            heightPx = if (isMobile) 44 else 50,
                            driverLoginReference = true
                        )
                    }
                }
            } else if (pwUser == null) {
                if (verificationCredentialsEntered || verificationMode) {
                    H2 { Text("Zugang verifizieren") }
                }
                if (verificationCredentialsEntered || verificationMode) {
                    P({ classes(NetzwerkLoginStyles.subheading); style { marginBottom(20.px) } }) {
                        Text(
                            if (verificationCredentialsEntered) {
                                "Bitte SnapKey eingeben und Zugang freigeben"
                            } else {
                                "Bitte zuerst mit E-Mail und Passwort anmelden"
                            }
                        )
                    }
                }
                if (!verificationCredentialsEntered) UnifiedLoginContent(
                    identifier = email,
                    password = password,
                    identifierPlaceholder = "E-Mail",
                    loading = loading,
                    errorText = loginEmailError ?: loginPwError,
                    forgotLabel = if (verificationMode) null else if (resetLoading) "..." else "Passwort vergessen",
                    switchPrefix = if (verificationMode) null else "Neu hier?",
                    switchLabel = if (verificationMode) null else "Registrieren",
                    onIdentifierChange = { email = it; loginEmailError = null },
                    onPasswordChange = { password = it; loginPwError = null },
                    onSubmit = ::doLogin,
                    onForgot = if (verificationMode) null else ::requestPasswordReset,
                    onSwitch = if (verificationMode) null else onRegister
                ) else Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                    if (!verificationCredentialsEntered) {
                        NetzwerkInput(InputType.Email, email, "E-Mail", loginEmailError, { email = it; loginEmailError = null }, ::doLogin, loading, loginStyle = true)
                        Div({ style { marginTop(6.px) } }) {
                            NetzwerkInput(InputType.Password, password, "Passwort", loginPwError, { password = it; loginPwError = null }, ::doLogin, loading, loginStyle = true)
                        }
                    } else {
                        NetzwerkInput(InputType.Text, snapKey, "SnapKey", null, { snapKey = it; loginPwError = null }, ::doLogin, loading, loginStyle = true)
                        loginPwError?.let { error ->
                            P({ classes(NetzwerkLoginStyles.errorParagraph) }) { Text(error) }
                        }
                    }
                    Div({ style { width(100.percent); marginTop(20.px) } }) {
                        UnifiedAccessButton(
                            label = when {
                                loading -> "..."
                                verificationCredentialsEntered -> "Zugang freigeben"
                                else -> "Anmelden"
                            },
                            onClick = ::doLogin,
                            enabled = !loading
                        )
                    }
                    if (!verificationMode) {
                        Span({
                            classes(UnifiedAccessStyles.textLink)
                            onClick { if (!loading && !resetLoading) requestPasswordReset() }
                        }) { Text(if (resetLoading) "..." else "Passwort vergessen") }
                    }
                    resetMessage?.let { msg ->
                        P({ classes(NetzwerkLoginStyles.subheading); style { marginTop(8.px) } }) { Text(msg) }
                    }
                    Div({ classes(UnifiedAccessStyles.switchRow) }) {
                        Span { Text("Neu hier?") }
                        Span({ classes(UnifiedAccessStyles.switchAction); onClick { onRegister() } }) { Text("Registrieren") }
                    }
                }
            } else {
                H2 { Text("Passwort ändern") }
                P({ classes(NetzwerkLoginStyles.subheading); style { marginBottom(20.px) } }) {
                    Text("Bei der ersten Anmeldung musst du dein Passwort ändern.")
                }
                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                    NetzwerkInput(InputType.Password, newPassword, "Neues Passwort", pwError, { newPassword = it; pwError = null }, ::doPasswordChange, loading, loginStyle = true)
                    PasswordRequirementHint(newPassword)
                    NetzwerkInput(InputType.Password, repeatPassword, "Passwort wiederholen", null, { repeatPassword = it; pwError = null }, ::doPasswordChange, loading, loginStyle = true)
                    Div({ style { width(100.percent); marginTop(20.px) } }) {
                        LumaticaButton(
                            label = if (loading) "..." else "Speichern",
                            onClick = ::doPasswordChange,
                            variant = LumaticaButtonVariant.Action,
                            enabled = !loading,
                            fillWidth = true,
                            heightPx = 44
                        )
                    }
                }
            }
        }
    }
}

@Composable
internal fun NetzwerkInput(
    type: InputType<String>,
    value: String,
    placeholderText: String,
    errorText: String?,
    onInput: (String) -> Unit,
    onEnter: () -> Unit,
    disabledInput: Boolean,
    loginStyle: Boolean = false
) {
    Div({ style { position(Position.Relative); width(100.percent) } }) {
        Input(type, attrs = {
            classes(if (loginStyle) LoginStyles.input else NetzwerkLoginStyles.regInput)
            if (loginStyle) classes(UnifiedAccessStyles.input)
            if (!loginStyle && errorText != null) classes(NetzwerkLoginStyles.regInputError)
            if (disabledInput) {
                disabled()
                classes(NetzwerkLoginStyles.regInputDisabled)
            }
            placeholder(placeholderText)
            value(value)
            onInput { onInput(it.value) }
            onKeyDown {
                if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                    it.preventDefault()
                    onEnter()
                }
            }
        })
        if (errorText != null) {
            if (loginStyle) {
                Div({ classes(LoginStyles.error) }) { Text(errorText) }
            } else {
                Span({
                    classes(NetzwerkLoginStyles.fieldErrorText)
                    style {
                        position(Position.Absolute)
                        property("right", "12px")
                        property("top", "50%")
                        property("transform", "translateY(-50%)")
                    }
                }) { Text(errorText) }
            }
        }
    }
}


